/**
 * V103-4-5 — pure helpers for the component boundary editor (ADR-0066 +
 * amendments): which programs use the component register, which period an
 * edit lands in, suggestion matching and default add targets, and the drift
 * report as editor rows. No I/O; unit-tested in __tests__/boundary-model.test.ts.
 */
import { GOVRAMP_PROGRAM_KEY, getProgramManifest, type BoundaryComponent, type BoundaryDriftItem, type BoundaryDriftReport, type BoundarySuggestion, type ProgramManifest } from '@aigovops/core';

/**
 * Programs whose Scope is the component boundary: GovRAMP (inventory
 * workbook) and the programs with persistent validation (FedRAMP 20x MAS).
 * The same rule the server's drift engine applies.
 */
export function usesComponentBoundary(m: Pick<ProgramManifest, 'key' | 'features'> | undefined | null): boolean {
  return !!m && (m.key === GOVRAMP_PROGRAM_KEY || m.features.persistentValidation === true);
}

/** The program's display label ("GovRAMP", "FedRAMP 20x"). */
export const programLabel = (key: string) => getProgramManifest(key)?.label ?? key;

/** A period whose audit has started is frozen (ADR-0066 A2); planned phases stay editable. */
export const isFrozenPhase = (phase: string | null | undefined) => phase === 'fieldwork' || phase === 'certified' || phase === 'superseded';

/**
 * Which boundary the editor opens on: the first period still open for
 * fieldwork prep (pre-Audit), else the standing boundary (null). A certified
 * program in ConMon has only frozen periods, and its live boundary is the
 * standing one, so it opens editable (ADR-0066 A2: later edits go to the
 * standing boundary or the next period).
 */
export function defaultBoundaryPeriod(periods: ReadonlyArray<{ period_id: string | null; phase?: string | null }>): string | null {
  return periods.find((p) => p.period_id && !isFrozenPhase(p.phase))?.period_id ?? null;
}

/** The period selector's option text: an open period is editable; a frozen one says it is read-only. */
export function periodOptionLabel(p: { label: string; phase?: string | null }): string {
  return isFrozenPhase(p.phase) ? `${p.label} · locked for audit (read-only)` : `${p.label} · open`;
}
export const STANDING_OPTION_LABEL = 'Standing boundary (current)';

/**
 * Where a scope edit lands: the period's own rows when it has them, else the
 * standing boundary (adding one row to a period that reads the standing scope
 * would make that one row the whole period).
 */
export function writePeriod(source: 'period' | 'standing' | 'empty', periodId: string | null): string | null {
  return source === 'period' ? periodId : null;
}

export type FederalAnswer = 'Yes' | 'No' | 'Not answered';
export function federalLabel(v: BoundaryComponent['federal_data']): FederalAnswer {
  return v === 'handles' || v === 'impacts' ? 'Yes' : v === 'neither' ? 'No' : 'Not answered';
}

/** "You run it" / "Inherited from AWS" / the provider's name. */
export function providerLabel(c: Pick<BoundaryComponent, 'inherited_from_service_id' | 'external_service_id'>, names: ReadonlyMap<string, string>): string {
  if (c.inherited_from_service_id) return `Inherited from ${names.get(c.inherited_from_service_id) ?? 'provider'}`;
  if (c.external_service_id) return names.get(c.external_service_id) ?? 'External service';
  return 'You run it';
}

export const KIND_LABEL: Record<BoundaryComponent['kind'], string> = {
  infrastructure: 'Host', container: 'Container', software: 'App', database: 'Database', managed_service: 'Managed service',
};

const DAY = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2 h ago" · "3 d ago" · "Oct 2" · "Never" (UTC). */
export function observedLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'Never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'Never';
  const diff = now.getTime() - t;
  if (diff < 60 * 60_000) return `${Math.max(1, Math.round(diff / 60_000))} min ago`;
  if (diff < DAY) return `${Math.round(diff / 3_600_000)} h ago`;
  if (diff < 7 * DAY) return `${Math.round(diff / DAY)} d ago`;
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** A suggestion that looks like an existing component: same kind, and one name is the other's stem ("orders-db-2" ~ "orders-db"). */
export function looksLike(s: Pick<BoundarySuggestion, 'name' | 'suggested_kind'>, components: ReadonlyArray<BoundaryComponent>): BoundaryComponent | null {
  const n = s.name.toLowerCase();
  let best: BoundaryComponent | null = null;
  for (const c of components) {
    const k = c.component_key.toLowerCase();
    if (k.length < 4 || c.kind !== s.suggested_kind) continue;
    if (n === k || n.startsWith(`${k}-`) || n.startsWith(`${k}_`) || k.startsWith(`${n}-`)) {
      if (!best || k.length > best.component_key.length) best = c;
    }
  }
  return best;
}

/**
 * Does the resource hold federal data? A tag says so when the connector
 * reports one (`federal_data` / `data_classification`); else the component it
 * looks like answers. Null = unknown (a person answers on accept).
 */
export function federalHint(s: Pick<BoundarySuggestion, 'tags'>, match: BoundaryComponent | null): 'yes' | 'no' | null {
  const tag = Object.entries(s.tags ?? {}).find(([k]) => /^(federal[_-]?data|data[_-]?classification)$/i.test(k))?.[1]?.toLowerCase();
  if (tag) {
    if (/^(yes|true|handles|impacts|federal|cui|fci)$/.test(tag)) return 'yes';
    if (/^(no|false|neither|public|none)$/.test(tag)) return 'no';
  }
  if (match?.federal_data) return match.federal_data === 'neither' ? 'no' : 'yes';
  return null;
}

export type AddChoice = 'both' | 'this' | 'workspace';
/** The split button's default: federal data → this program and the other; no federal data → the workspace register only; unknown → this program. */
export function defaultAdd(hint: 'yes' | 'no' | null, hasOther: boolean): AddChoice {
  if (hint === 'yes') return hasOther ? 'both' : 'this';
  if (hint === 'no') return 'workspace';
  return 'this';
}
export function addChoiceLabel(choice: AddChoice, thisLabel: string, otherLabel: string | null): string {
  if (choice === 'both') return `Add to ${thisLabel}, also ${otherLabel}`;
  if (choice === 'this') return `Add to ${thisLabel} boundary`;
  return 'Add to workspace only';
}

// ---- drift → editor rows ---------------------------------------------------------------------------

export type DriftTone = 'bad' | 'warn' | 'neutral';
export interface DriftRow {
  key: string;
  kind: BoundaryDriftItem['kind'];
  tone: DriftTone;
  chip: string;
  title: string;
  detail: string;
  component_ids: string[];
  external_service_id: string | null;
  items: BoundaryDriftItem[];
}

const FIELD_LABEL: Record<string, string> = {
  virtual: 'virtual', public: 'public', location: 'location', asset_type: 'asset type', os_name_and_version: 'OS',
  baseline_configuration_name: 'baseline', software_database_vendor: 'vendor', software_database_name_and_version: 'version',
  ip_address_or_dns_name: 'IP or DNS name',
};
export const fieldLabel = (f: string) => FIELD_LABEL[f] ?? f.replace(/_/g, ' ');

/** One row per issue; missing-field gaps are grouped into one row with a "Fill in N" action. */
export function driftRows(report: Pick<BoundaryDriftReport, 'drift' | 'gaps'>, serviceNames: ReadonlyMap<string, string>): DriftRow[] {
  const rows: DriftRow[] = [];
  const ref = (i: BoundaryDriftItem) => i.component_key ?? (i.external_service_id ? serviceNames.get(i.external_service_id) ?? i.external_service_id : 'Boundary');
  for (const i of report.drift) {
    const base = { key: `${i.kind}:${i.component_id ?? i.external_service_id ?? ''}`, kind: i.kind, component_ids: i.component_id ? [i.component_id] : [], external_service_id: i.external_service_id ?? null, items: [i] };
    switch (i.kind) {
      case 'asset_retired': rows.push({ ...base, tone: 'bad', chip: 'Retired', title: ref(i), detail: 'Its linked asset was retired' }); break;
      case 'not_observed': rows.push({ ...base, tone: 'warn', chip: `Not observed · ${i.days_unobserved ?? 0} days`, title: ref(i), detail: 'Its connector no longer lists it' }); break;
      case 'provider_retired': rows.push({ ...base, tone: 'bad', chip: 'Provider retired', title: ref(i), detail: i.external_service_id ? `${serviceNames.get(i.external_service_id) ?? 'The provider'} was retired` : 'Its provider was retired' }); break;
      case 'system_left_boundary': rows.push({ ...base, tone: 'warn', chip: 'System left', title: ref(i), detail: 'Its system is no longer in this boundary' }); break;
      case 'asset_unscoped': rows.push({ ...base, tone: 'warn', chip: 'Asset not scoped', title: ref(i), detail: 'Its asset is in no open period' }); break;
      case 'asset_excluded': rows.push({ ...base, tone: 'warn', chip: 'Asset out of scope', title: ref(i), detail: 'Its asset is categorized out of scope' }); break;
      default: rows.push({ ...base, tone: 'neutral', chip: 'Changed', title: ref(i), detail: 'Its asset changed after it was linked' });
    }
  }
  const missing = report.gaps.filter((g) => g.kind === 'missing_inventory_fields');
  for (const g of report.gaps) {
    if (g.kind === 'missing_inventory_fields') continue;
    const base = { key: `${g.kind}:${g.component_id ?? g.external_service_id ?? ''}`, kind: g.kind, component_ids: g.component_id ? [g.component_id] : [], external_service_id: g.external_service_id ?? null, items: [g] };
    if (g.kind === 'govramp_subprocessor_unverified') rows.push({ ...base, tone: 'bad', chip: 'Caps GovRAMP at Provisional', title: ref(g), detail: 'Not GovRAMP or FedRAMP verified' });
    else if (g.kind === 'federal_data_unanswered') rows.push({ ...base, tone: 'neutral', chip: 'Incomplete', title: ref(g), detail: 'Federal data not answered' });
    else rows.push({ ...base, tone: 'neutral', chip: 'Incomplete', title: ref(g), detail: `Third-party fields: ${(g.fields ?? []).map(fieldLabel).join(', ')}` });
  }
  if (missing.length) {
    rows.push({
      key: 'missing_inventory_fields', kind: 'missing_inventory_fields', tone: 'neutral', chip: 'Incomplete',
      title: `${missing.length} component${missing.length === 1 ? '' : 's'} missing inventory fields`,
      detail: missing.map((g) => `${g.component_key} · ${(g.fields ?? []).map(fieldLabel).join(', ')}`).join('  ·  '),
      component_ids: missing.map((g) => g.component_id!).filter(Boolean), external_service_id: null, items: missing,
    });
  }
  return rows;
}

/** The component(s) whose provider is an unverified sub-processor (the drift row's "Plan a replacement" target). */
export function componentsUsing(serviceId: string, components: ReadonlyArray<BoundaryComponent>): BoundaryComponent[] {
  return components.filter((c) => c.external_service_id === serviceId || c.inherited_from_service_id === serviceId);
}
